import React, { useCallback, useEffect, useMemo, useState } from "react";
import { History, Search, Trash2 } from "lucide-react";
import LoadingButton from "../LoadingButton";
import { inspectionsAPI } from "../../../../../../services/api";

const norm = (v) => (v ?? "").toString();
const lower = (v) => norm(v).toLowerCase();
const ISSUE_ASSIGNEE_ROLES = new Set(["foreman", "general_foreman", "supervisor"]);

const roleLabel = (role) =>
  (role || "")
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const normalizeRole = (role) => lower(role).trim().replace(/\s+/g, "_");

const optionRole = (opt) => {
  const directRole = normalizeRole(opt?.role);
  if (directRole) return directRole;

  const labelRole = /\(([^)]+)\)\s*$/.exec(opt?.label || "")?.[1];
  return normalizeRole(labelRole);
};

function SearchableResponsibleSelect({
  options,
  value: selectedValue,
  onChange,
  disabled,
  loading,
  fallbackLabel = "Unassigned",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = useMemo(
    () => (options || []).find((opt) => Number(opt.value) === Number(selectedValue)),
    [options, selectedValue],
  );

  const selectedLabel = selected?.label || (selectedValue ? fallbackLabel : "Unassigned");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options || [];

    return (options || []).filter((opt) => {
      const role = optionRole(opt);
      const haystack = [
        opt.label,
        opt.email,
        opt.role,
        role,
        roleLabel(role),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [options, query]);

  const choose = (nextValue) => {
    onChange?.(nextValue);
    setQuery("");
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="mt-1 flex w-full items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-left text-sm disabled:bg-gray-100 disabled:text-gray-500"
      >
        <span className="min-w-0 truncate">
          {loading ? "Loading team..." : selectedLabel}
        </span>
        {!disabled && <Search className="h-4 w-4 shrink-0 text-gray-400" />}
      </button>

      {open && !disabled && (
        <div className="absolute z-40 mt-1 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, or role..."
              className="w-full text-sm outline-none"
            />
          </div>

          <div className="max-h-56 overflow-y-auto py-1">
            <button
              type="button"
              onClick={() => choose(null)}
              className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
            >
              <span className="block font-medium text-gray-800">Unassigned</span>
              <span className="block text-xs text-gray-400">No responsible person</span>
            </button>

            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-500">
                No matching Foreman, General Foreman, or Supervisor.
              </div>
            ) : (
              filtered.map((opt) => {
                const role = optionRole(opt);
                return (
                  <button
                    key={opt.key || opt.value}
                    type="button"
                    onClick={() => choose(Number(opt.value))}
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-orange-50"
                  >
                    <span className="block truncate font-medium text-gray-900">
                      {opt.label}
                    </span>
                    <span className="block truncate text-xs text-gray-500">
                      {[roleLabel(role), opt.email].filter(Boolean).join(" • ") || "Project team member"}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const statusBadgeClass = (status) => {
  const s = lower(status);
  if (s === "closed") return "border-gray-200 bg-gray-100 text-gray-700";
  if (s === "responded" || s === "answered") return "border-blue-100 bg-blue-50 text-blue-700";
  if (s === "reopened") return "border-orange-100 bg-orange-50 text-orange-700";
  if (s === "pending") return "border-amber-100 bg-amber-50 text-amber-700";
  return "border-emerald-100 bg-emerald-50 text-emerald-700";
};

const severityAccentClass = (severity) => {
  const s = lower(severity);
  if (s === "critical") return "bg-red-500";
  if (s === "major") return "bg-orange-500";
  return "bg-emerald-500";
};

const severityBadgeClass = (severity) => {
  const s = lower(severity);
  if (s === "critical") return "border-red-100 bg-red-50 text-red-700";
  if (s === "major") return "border-orange-100 bg-orange-50 text-orange-700";
  return "border-emerald-100 bg-emerald-50 text-emerald-700";
};

const FindingsTab = ({
  projectId,
  inspectionId,
  inspectionSiteLocation,
  forbidden,
  loading,
  findings,

  teamOptions,
  teamLoading,

  onAddFinding,
  adding,

  onSaveFinding,
  onCloseFinding,
  canCloseFinding = false,
  onDeleteFinding,

  savingMap,
  closingMap,
  deletingMap,
}) => {
  // Local drafts: keep issues editing encapsulated in this tab.
  const [drafts, setDrafts] = useState({});
  const [historyFinding, setHistoryFinding] = useState(null);
  const [historyComments, setHistoryComments] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const issueAssigneeOptions = useMemo(() => {
    return (teamOptions || []).filter((opt) =>
      ISSUE_ASSIGNEE_ROLES.has(optionRole(opt)),
    );
  }, [teamOptions]);

  const startDraftIfNeeded = useCallback(
    (f) => {
      setDrafts((prev) => {
        if (prev[f.id]) return prev;
        return {
          ...prev,
          [f.id]: {
            description: f.description || "",
            severity: f.severity || "minor",
            location: f.location || inspectionSiteLocation || "",
            status: f.status || "open",
            corrective_action: f.corrective_action || "",
            responsible_user_id: f.responsible_user_id ?? null,
            due_date: f.due_date || "",
          },
        };
      });
    },
    [inspectionSiteLocation],
  );

  const updateDraft = useCallback((id, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), ...patch },
    }));
  }, []);

  const cancelDraft = useCallback((f) => {
    setDrafts((prev) => {
      const copy = { ...prev };
      delete copy[f.id];
      return copy;
    });
  }, []);

  const isDirty = useCallback(
    (f) => {
      const d = drafts[f.id];
      if (!d) return false;
      return (
        norm(d.description) !== norm(f.description) ||
        norm(d.severity) !== norm(f.severity) ||
        norm(d.location) !== norm(f.location) ||
        norm(d.status) !== norm(f.status) ||
        norm(d.corrective_action) !== norm(f.corrective_action) ||
        norm(d.responsible_user_id) !== norm(f.responsible_user_id) ||
        norm(d.due_date) !== norm(f.due_date)
      );
    },
    [drafts],
  );

  const total = useMemo(
    () => (forbidden ? "—" : (findings?.length ?? 0)),
    [forbidden, findings],
  );

  const openHistory = useCallback(async (finding) => {
    if (!projectId || !inspectionId || !finding?.id) return;
    setHistoryFinding(finding);
    setHistoryLoading(true);
    try {
      const res = await inspectionsAPI.listFindingComments(projectId, inspectionId, finding.id);
      setHistoryComments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Failed to load issue history.");
      setHistoryComments([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [projectId, inspectionId]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">Issues</div>
          <div className="text-xs text-gray-500">Total: {total}</div>
        </div>

        {!forbidden && (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <LoadingButton
              loading={adding}
              loadingText="Adding…"
              onClick={onAddFinding}
              className="rounded-lg bg-orange-500 px-3 py-2 text-sm text-white hover:bg-orange-600"
            >
              + Add Issue
            </LoadingButton>
          </div>
        )}
      </div>

      {forbidden && (
        <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
          You don’t have permission to view issues for this inspection.
        </div>
      )}

      {!forbidden && loading && (
        <div className="text-sm text-gray-500">Loading issues…</div>
      )}

      {!forbidden && !loading && (findings?.length ?? 0) === 0 && (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-5 text-sm text-gray-500">
          No issues yet.
        </div>
      )}

      {!forbidden && !loading && (findings?.length ?? 0) > 0 && (
        <div className="space-y-3">
          {findings.map((f) => {
            const draft = drafts[f.id] || null;
            const saving = !!savingMap?.[f.id];
            const closing = !!closingMap?.[f.id];
            const deleting = !!deletingMap?.[f.id];
            const siteLocation = draft ? (draft.location || "") : (inspectionSiteLocation || f.location || "");

            return (
              <div
                key={f.id}
                className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 pl-5 shadow-sm ring-1 ring-gray-100 transition hover:-translate-y-0.5 hover:border-orange-200 hover:shadow-md"
              >
                <div className={`absolute inset-y-0 left-0 w-1.5 ${severityAccentClass(f.severity)}`} />

                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="min-w-0 break-words text-sm font-bold text-gray-950">
                        {f.finding_number || `Issue #${f.id}`}
                      </div>
                      <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize ${statusBadgeClass(f.status)}`}>
                        {f.status || "open"}
                      </span>
                      {f.responsible_user_id && f.is_released === false ? (
                        <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                          Prepared — waiting for inspection approval
                        </span>
                      ) : null}
                      <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize ${severityBadgeClass(f.severity)}`}>
                        {f.severity || "minor"}
                      </span>
                    </div>

                  </div>

                  <div className="flex flex-wrap justify-end gap-2">
                    {canCloseFinding ? (
                      <LoadingButton
                        loading={closing}
                        loadingText="Closing…"
                        disabled={(f.status || "").toLowerCase() === "closed"}
                        onClick={() => onCloseFinding(f.id)}
                        spinnerSize="small"
                        className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs text-white hover:bg-black"
                      >
                        Approve / Close
                      </LoadingButton>
                    ) : null}

                    <button
                      type="button"
                      onClick={() => openHistory(f)}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs hover:bg-gray-50"
                    >
                      <History className="h-3.5 w-3.5" />
                      History
                    </button>

                    {draft ? (
                      <>
                        <button
                          type="button"
                          onClick={() => cancelDraft(f)}
                          disabled={saving}
                          className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs text-gray-800 hover:bg-gray-200 disabled:opacity-50"
                        >
                          Cancel
                        </button>

                        <LoadingButton
                          loading={saving}
                          loadingText="Saving…"
                          disabled={!isDirty(f)}
                          onClick={() =>
                            onSaveFinding(f, drafts[f.id], () => cancelDraft(f))
                          }
                          spinnerSize="small"
                          className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-700"
                        >
                          Save
                        </LoadingButton>
                      </>
                    ) : null}
                  </div>
                </div>

                {!draft && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => startDraftIfNeeded(f)}
                      className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs hover:bg-gray-50"
                    >
                      Edit
                    </button>

                    <LoadingButton
                      loading={deleting}
                      loadingText="Deleting…"
                      onClick={() => onDeleteFinding(f.id)}
                      spinnerSize="small"
                      className="rounded-lg bg-red-600 px-3 py-1.5 text-xs text-white hover:bg-red-700"
                    >
                      <span className="inline-flex items-center gap-1">
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </span>
                    </LoadingButton>
                  </div>
                )}

                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <label className="text-xs font-medium text-gray-600">Severity</label>
                    <select
                      disabled={!draft}
                      value={(draft?.severity ?? f.severity) || "minor"}
                      onChange={(e) =>
                        updateDraft(f.id, { severity: e.target.value })
                      }
                      className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm disabled:bg-gray-100"
                    >
                      <option value="minor">minor</option>
                      <option value="major">major</option>
                      <option value="critical">critical</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-600">
                      Responsible Person
                    </label>
                    <SearchableResponsibleSelect
                      disabled={!draft}
                      loading={teamLoading}
                      options={issueAssigneeOptions}
                      value={draft?.responsible_user_id ?? f.responsible_user_id ?? ""}
                      fallbackLabel={f.responsible_user_name || f.responsible_user_email || "Unassigned"}
                      onChange={(nextValue) =>
                        updateDraft(f.id, {
                          responsible_user_id: nextValue ? Number(nextValue) : null,
                        })
                      }
                    />
                    <p className="mt-1 text-[11px] text-gray-400">
                      Assignable roles: Foreman, General Foreman, Supervisor.
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-xs font-medium text-gray-600">Description</label>
                    <textarea
                      disabled={!draft}
                      value={(draft?.description ?? f.description) || ""}
                      onChange={(e) =>
                        updateDraft(f.id, { description: e.target.value })
                      }
                      className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm disabled:bg-gray-100"
                      rows={3}
                      placeholder="Describe the issue..."
                    />
                  </div>

                  {(f.response_text || f.responded_at) && (
                    <div className="md:col-span-2 rounded-xl border border-emerald-100 bg-emerald-50 p-3">
                      <div className="text-xs font-semibold text-emerald-800">
                        Assigned person response
                      </div>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">
                        {f.response_text || "-"}
                      </p>
                      <div className="mt-2 text-xs text-gray-600">
                        Responded by: {f.responded_by_name || f.responded_by || "-"} • Responded at: {f.responded_at || "-"}
                      </div>
                    </div>
                  )}

                  <div className="md:col-span-2">
                    <label className="text-xs font-medium text-gray-600">
                      Corrective Action
                    </label>
                    <textarea
                      disabled={!draft}
                      value={(draft?.corrective_action ?? f.corrective_action) || ""}
                      onChange={(e) =>
                        updateDraft(f.id, { corrective_action: e.target.value })
                      }
                      className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm disabled:bg-gray-100"
                      rows={2}
                      placeholder="Proposed corrective action..."
                    />
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-600">Due Date</label>
                    <input
                      type="date"
                      disabled={!draft}
                      value={(draft?.due_date ?? f.due_date) || ""}
                      onChange={(e) =>
                        updateDraft(f.id, { due_date: e.target.value })
                      }
                      className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm disabled:bg-gray-100"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-600">Site location</label>
                    <input
                      disabled={!draft}
                      value={siteLocation || ""}
                      onChange={(e) =>
                        updateDraft(f.id, { location: e.target.value })
                      }
                      className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm disabled:bg-gray-100"
                      placeholder="Site location / area..."
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {historyFinding && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b p-4">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Issue History</p>
                <h3 className="break-words text-lg font-semibold text-gray-900">
                  {historyFinding.finding_number || `Issue #${historyFinding.id}`}
                </h3>
              </div>
              <button type="button" onClick={() => setHistoryFinding(null)} className="rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50">
                Close
              </button>
            </div>
            <div className="max-h-[65vh] space-y-3 overflow-y-auto p-4">
              {historyLoading ? (
                <div className="text-sm text-gray-500">Loading history…</div>
              ) : historyComments.length === 0 ? (
                <div className="rounded-lg border border-dashed p-4 text-sm text-gray-500">No history yet.</div>
              ) : (
                historyComments.map((c) => (
                  <div key={c.id} className="rounded-xl border bg-gray-50 p-3 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
                      <span className="font-semibold text-gray-800">{c.user_name || `User #${c.user_id}`} {c.user_role ? `• ${c.user_role}` : ""}</span>
                      <span>{c.created_at ? new Date(c.created_at).toLocaleString() : ""}</span>
                    </div>
                    <div className="mt-1 whitespace-pre-wrap break-words text-gray-800">{c.body}</div>
                    {(c.from_status || c.to_status) && (
                      <div className="mt-2 text-xs text-gray-500">{c.from_status || "-"} → {c.to_status || "-"}</div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(FindingsTab);
